// Reusable bulk-upload actions block for master pages (Projects / Vendors / Clients / Members).
// Renders three pieces:
//   1. Hidden file input
//   2. "Template" download button
//   3. "Bulk Upload" button
//   4. A result banner (created / skipped / per-row errors)
//
// Props:
//   master       — slug used in routes: `${master}-template` and `${master}-bulk-upload`
//                  e.g. "projects", "vendors", "clients", "members"
//   filename     — pretty filename for the downloaded template (e.g. "projects_master_template.xlsx")
//   onUploaded   — callback fired after a successful upload (the parent typically reloads its list)
//   maxRows      — display-only hint; defaults to 500

import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Download, Upload, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function BulkUploadButtons({ master, filename, onUploaded, maxRows = 500, templateLabel = "Template", uploadLabel = "Bulk Upload" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [, setResult] = useState(null);

  async function downloadTemplate() {
    try {
      const res = await api.get(`/${master}-template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download template");
    }
  }

  async function handleBulkUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/${master}-bulk-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      // Surface the result to the parent (via callback + via a global toast).
      const created = data.created || 0;
      const updated = data.updated || 0;
      const skipped = data.skipped || 0;
      if (created > 0 || updated > 0) {
        const parts = [];
        if (created) parts.push(`${created} created`);
        if (updated) parts.push(`${updated} updated`);
        if (skipped) parts.push(`${skipped} skipped`);
        toast.success(parts.join(" · "));
      } else if (skipped > 0) {
        toast.error(`No rows imported · ${skipped} row${skipped === 1 ? "" : "s"} skipped`);
      }
      // Show a one-time detail banner via a custom event so parents can render it inline if needed.
      window.dispatchEvent(new CustomEvent("master-bulk-upload-result", { detail: { master, ...data } }));
      onUploaded?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
        data-testid={`${master}-bulk-upload-input`} onChange={handleBulkUpload} />
      <button data-testid={`${master}-download-template-btn`} onClick={downloadTemplate}
        title={`Download ${master} Excel template (max ${maxRows} rows per upload)`}
        className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
        <Download size={14} /> {templateLabel}
      </button>
      <button data-testid={`${master}-bulk-upload-btn`} disabled={uploading}
        onClick={() => fileRef.current?.click()}
        className="bg-white border border-[#F97316] hover:bg-orange-50 text-[#F97316] px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
        <Upload size={14} /> {uploading ? "Uploading…" : uploadLabel}
      </button>
    </>
  );
}

// Optional banner the parent page can mount to show created / skipped + per-row errors
// after a bulk-upload. It listens for the global "master-bulk-upload-result" event.
export function BulkUploadResultBanner({ master }) {
  const [result, setResult] = useState(null);
  // Sprint 2026-Q1 — wire the event listener inside an effect so we
  // don't attach a fresh listener on every render. Previously the
  // listener was added directly in the render body (no cleanup), which
  // caused N listeners to accumulate over a component's lifetime and
  // also leaked across page navigations.
  useEffect(() => {
    function onResult(ev) {
      if (ev.detail?.master === master) setResult(ev.detail);
    }
    window.addEventListener("master-bulk-upload-result", onResult);
    return () => window.removeEventListener("master-bulk-upload-result", onResult);
  }, [master]);
  if (!result) return null;
  return (
    <div className="mb-4 border border-slate-200 bg-white px-4 py-3" data-testid={`${master}-bulk-result`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5">
            <CheckCircle2 size={12} /> {result.created} created
          </span>
          {(result.updated || 0) > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-blue-800 bg-blue-50 border border-blue-200 px-2 py-0.5">
              <CheckCircle2 size={12} /> {result.updated} updated
            </span>
          )}
          {result.skipped > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5">
              <AlertTriangle size={12} /> {result.skipped} skipped
            </span>
          )}
          <span className="text-xs text-slate-500">Total rows: <span className="font-bold tabular">{result.total_rows}</span></span>
        </div>
        <button onClick={() => setResult(null)} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
      </div>
      {result.errors?.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-600 hover:text-[#F97316] font-bold">
            Show {result.errors.length} skipped row{result.errors.length === 1 ? "" : "s"}
          </summary>
          <ul className="mt-2 pl-4 list-disc space-y-0.5">
            {result.errors.map((er, i) => (
              <li key={i} className="text-amber-900"><span className="font-mono text-slate-500">Row {er.row}:</span> {er.error}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
